import { supabase } from './supabase';

// Retry configuration
const MAX_RETRIES = 3;
const BASE_DELAY = 1000; // 1 second

// Helper function to wait for a specified time
const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Helper function to check if an error is retryable
function isRetryableError(error: any): boolean {
  // HTTP status codes that should be retried
  if (error.status === 502 || error.status === 503 || error.status === 504 || error.status === 500) {
    return true;
  }
  
  // JSON parse errors (when server returns HTML instead of JSON)
  if (error.message && error.message.includes('Unexpected token')) {
    return true;
  }
  
  // Supabase specific server errors
  if (error.message && (
    error.message.includes('Bad Gateway') ||
    error.message.includes('Service Unavailable') ||
    error.message.includes('Gateway Timeout') ||
    error.message.includes('Internal Server Error')
  )) {
    return true;
  }
  
  return false;
}

// Helper function to implement exponential backoff retry
async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  retries: number = MAX_RETRIES,
  delay: number = BASE_DELAY
): Promise<T> {
  try {
    return await operation();
  } catch (error: any) {
    console.error('Storage operation error:', error);
    
    if (retries > 0 && isRetryableError(error)) {
      const statusCode = error.status || 'unknown';
      await wait(delay);
      return retryWithBackoff(operation, retries - 1, delay * 2);
    }
    throw error;
  }
}

// Upload a base64 image to storage
export async function uploadBase64Image(
  base64Data: string, 
  bucket: string = 'propertyimages',
  filename: string = `${Date.now()}.jpg` 
) {
  return retryWithBackoff(async () => {
  try {
    // Convert base64 to blob
    const base64Response = await fetch(base64Data);
    const blob = await base64Response.blob();
    
    // Convert blob to file
    const file = new File([blob], filename, { type: 'image/jpeg' });
    
    // Upload to Supabase storage with owner metadata
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(filename, file, {
        cacheControl: '3600',
        upsert: true,
        contentType: 'image/jpeg'
      });

    if (error) {
      throw error;
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(filename);

    return urlData.publicUrl;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
  });
}

// Upload a file to storage
export async function uploadFile(
  file: File,
  bucket: string = 'propertyimages',
  options = { upsert: true }
) {
  return retryWithBackoff(async () => {
  try {
    if (!file || !file.type.startsWith('image/')) {
      throw new Error('Invalid file or file type');
    }
    
    const fileName = file.name.replace(/^\/+|\/+$/g, '');

    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: options.upsert,
        contentType: file.type
      });

    if (error) {
      throw error;
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(fileName);

    return urlData.publicUrl;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
  });
}